import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, PaginationEllipsis } from "@/components/ui/pagination";
import { BookOpen, Eye, Trash2, CalendarDays, TableIcon, ExternalLink, Search, X } from "lucide-react";
import { format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { BookingsCalendar } from "./BookingsCalendar";

const statusColors: Record<string, string> = {
  pending: "bg-yellow-500/15 text-yellow-700 border-yellow-200",
  confirmed: "bg-blue-500/15 text-blue-700 border-blue-200",
  in_progress: "bg-purple-500/15 text-purple-700 border-purple-200",
  completed: "bg-green-500/15 text-green-700 border-green-200",
  cancelled: "bg-red-500/15 text-red-700 border-red-200",
};

const statusLabels: Record<string, string> = {
  pending: "Pending",
  confirmed: "Confirmed",
  in_progress: "In Progress",
  completed: "Completed",
  cancelled: "Cancelled",
};

const estatusRecogidaColors: Record<string, string> = {
  "No Confirmado": "bg-yellow-500/15 text-yellow-700 border-yellow-200",
  "Confirmado": "bg-blue-500/15 text-blue-700 border-blue-200",
  "Recogido": "bg-green-500/15 text-green-700 border-green-200",
  "No Show": "bg-red-500/15 text-red-700 border-red-200",
};

const estatusRecogidaOptions = ["No Confirmado", "Confirmado", "Recogido", "No Show"];

const paymentMethodLabels: Record<string, string> = {
  cash: "Cash",
  paypal: "PayPal",
  stripe: "Card",
};

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  if (!value) return null;
  return (
    <div className="grid grid-cols-3 gap-2 py-2 border-b border-border last:border-0">
      <span className="text-sm text-muted-foreground font-sans">{label}</span>
      <span className="col-span-2 text-sm font-sans text-foreground">{value}</span>
    </div>
  );
}

export function BookingsSection() {
  const [rows, setRows] = useState<any[]>([]);
  const { toast } = useToast();

  // Filtros
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [pickupStatusFilter, setPickupStatusFilter] = useState("all");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
  const [paymentMethodFilter, setPaymentMethodFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 30;

  const load = async () => {
    const { data, error } = await supabase.from("bookings").select("*, vehicles(name)").order("created_at", { ascending: false });
    if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    setRows(data || []);
  };

  useEffect(() => { load(); }, []);

  const updateField = async (id: string, field: string, value: string) => {
    const { error } = await supabase.from("bookings").update({ [field]: value } as any).eq("id", id);
    if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    load();
  };

  const deleteBooking = async (id: string) => {
    const { error } = await supabase.from("bookings").delete().eq("id", id);
    if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    toast({ title: "Eliminada", description: "La reserva ha sido eliminada." });
    load();
  };

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (pickupStatusFilter !== "all" && (r.estatus_recojida || "No Confirmado") !== pickupStatusFilter) return false;
      if (paymentStatusFilter !== "all" && r.payment_status !== paymentStatusFilter) return false;
      if (paymentMethodFilter !== "all" && r.payment_method !== paymentMethodFilter) return false;
      if (dateFrom) {
        const d = r.pickup_date ? new Date(r.pickup_date) : null;
        if (!d || d < new Date(dateFrom + "T00:00:00")) return false;
      }
      if (dateTo) {
        const d = r.pickup_date ? new Date(r.pickup_date) : null;
        if (!d || d > new Date(dateTo + "T23:59:59")) return false;
      }
      if (term) {
        const hay = [
          r.booking_code, r.customer_name, r.customer_email, r.customer_phone,
          r.pickup_location, r.dropoff_location, r.flight_number, r.flight_number_return,
          (r.vehicles as any)?.name,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [rows, searchTerm, statusFilter, pickupStatusFilter, paymentStatusFilter, paymentMethodFilter, dateFrom, dateTo]);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, statusFilter, pickupStatusFilter, paymentStatusFilter, paymentMethodFilter, dateFrom, dateTo]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedRows = filteredRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const resetFilters = () => {
    setSearchTerm(""); setStatusFilter("all"); setPickupStatusFilter("all");
    setPaymentStatusFilter("all"); setPaymentMethodFilter("all"); setDateFrom(""); setDateTo("");
  };

  const hasActiveFilters = !!(searchTerm || statusFilter !== "all" || pickupStatusFilter !== "all" ||
    paymentStatusFilter !== "all" || paymentMethodFilter !== "all" || dateFrom || dateTo);

  const pageNumbers = useMemo(() => {
    const pages: (number | "ellipsis")[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (safePage > 3) pages.push("ellipsis");
      const start = Math.max(2, safePage - 1);
      const end = Math.min(totalPages - 1, safePage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (safePage < totalPages - 2) pages.push("ellipsis");
      pages.push(totalPages);
    }
    return pages;
  }, [totalPages, safePage]);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Reservas</h2>
          <p className="text-muted-foreground text-sm mt-1">
            Mostrando <Badge variant="secondary" className="mx-1">{filteredRows.length}</Badge> de <Badge variant="secondary" className="ml-1">{rows.length}</Badge> reservas
          </p>
        </div>
      </div>

      <Tabs defaultValue="table" className="w-full">
        <TabsList>
          <TabsTrigger value="table" className="gap-1.5">
            <TableIcon className="h-4 w-4" /> Table
          </TabsTrigger>
          <TabsTrigger value="calendar" className="gap-1.5">
            <CalendarDays className="h-4 w-4" /> Calendar
          </TabsTrigger>
        </TabsList>

        <TabsContent value="table">
          {/* Barra de filtros */}
          <Card className="mb-4">
            <CardContent className="p-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="lg:col-span-2">
                  <Label className="text-xs">Buscar (código, nombre, email, teléfono, ruta...)</Label>
                  <div className="relative mt-1">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      className="pl-8"
                      placeholder="Ej: TXB-AB12, juan@..., +1809..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Estado</Label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {Object.entries(statusLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Estatus Recogida</Label>
                  <Select value={pickupStatusFilter} onValueChange={setPickupStatusFilter}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {estatusRecogidaOptions.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Estado de Pago</Label>
                  <Select value={paymentStatusFilter} onValueChange={setPaymentStatusFilter}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="unpaid">Unpaid</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Método de Pago</Label>
                  <Select value={paymentMethodFilter} onValueChange={setPaymentMethodFilter}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="paypal">PayPal</SelectItem>
                      <SelectItem value="stripe">Card</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Pickup desde</Label>
                  <Input type="date" className="mt-1" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">Pickup hasta</Label>
                  <Input type="date" className="mt-1" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                </div>
              </div>
              {hasActiveFilters && (
                <div className="flex justify-end">
                  <Button variant="outline" size="sm" onClick={resetFilters} className="gap-1.5">
                    <X className="h-3.5 w-3.5" /> Limpiar filtros
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0">
              {filteredRows.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <BookOpen className="h-10 w-10 mb-3 opacity-40" />
                  <p>{rows.length === 0 ? "No hay reservas todavía" : "No hay reservas que coincidan con los filtros"}</p>
                </div>
              ) : (
                <div className="overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Code</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Route</TableHead>
                        <TableHead>Vehicle</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead>Payment</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Est. Recogida</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedRows.map(row => (
                        <TableRow key={row.id}>
                          <TableCell className="font-mono text-xs font-bold text-primary">{row.booking_code || "—"}</TableCell>
                          <TableCell>
                            <div className="font-medium text-sm">{row.customer_name}</div>
                            <div className="text-xs text-muted-foreground">{row.customer_phone}</div>
                          </TableCell>
                          <TableCell>
                            <div className="text-xs max-w-[120px] truncate">{row.pickup_location}</div>
                            <div className="text-xs max-w-[120px] truncate text-muted-foreground">→ {row.dropoff_location}</div>
                            <Badge variant="secondary" className="text-[10px] mt-1">{row.route_type}</Badge>
                          </TableCell>
                          <TableCell className="text-sm">{(row.vehicles as any)?.name || "—"}</TableCell>
                          <TableCell className="font-semibold text-green-700">${row.total_price}</TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <Badge variant="outline" className={row.payment_status === "paid" ? "bg-green-500/15 text-green-700 border-green-200" : "bg-yellow-500/15 text-yellow-700 border-yellow-200"}>
                                {row.payment_status}
                              </Badge>
                              <div className="text-[10px] text-muted-foreground">{paymentMethodLabels[row.payment_method] || row.payment_method || "—"}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Select value={row.status} onValueChange={v => updateField(row.id, "status", v)}>
                              <SelectTrigger className="h-7 w-[120px] text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {Object.entries(statusLabels).map(([k, v]) => (
                                  <SelectItem key={k} value={k}>{v}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Select value={row.estatus_recojida || "No Confirmado"} onValueChange={v => updateField(row.id, "estatus_recojida", v)}>
                              <SelectTrigger className={`h-7 w-[130px] text-xs border ${estatusRecogidaColors[row.estatus_recojida || "No Confirmado"] || ""}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {estatusRecogidaOptions.map(opt => (
                                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-xs">{row.pickup_date ? formatInTimeZone(new Date(row.pickup_date), "UTC", "MMM d, yyyy HH:mm") : "—"}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-7 w-7"><Eye className="h-4 w-4" /></Button>
                                </DialogTrigger>
                                <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                                  <DialogHeader>
                                    <DialogTitle className="font-display">Booking Details</DialogTitle>
                                  </DialogHeader>
                                  <div className="space-y-1">
                                    <DetailRow label="Booking Code" value={<span className="font-mono font-bold text-primary">{row.booking_code || "—"}</span>} />
                                    <DetailRow label="Customer" value={row.customer_name} />
                                    <DetailRow label="Email" value={row.customer_email} />
                                    <DetailRow label="Phone" value={row.customer_phone} />
                                    <DetailRow label="Pickup" value={row.pickup_location} />
                                    <DetailRow label="Drop-off" value={row.dropoff_location} />
                                    <DetailRow label="Route Type" value={row.route_type} />
                                    <DetailRow label="Pickup Date" value={formatInTimeZone(new Date(row.pickup_date), "UTC", "PPP 'at' p")} />
                                    {row.return_date && <DetailRow label="Return Date" value={formatInTimeZone(new Date(row.return_date), "UTC", "PPP 'at' p")} />}
                                    <DetailRow label="Passengers" value={row.passengers} />
                                    <DetailRow label="Vehicle" value={(row.vehicles as any)?.name} />
                                    <DetailRow label="Distance" value={row.distance_km ? `${row.distance_km} km` : null} />
                                    <DetailRow label="Est. Time" value={row.estimated_time_min ? `${row.estimated_time_min} min` : null} />
                                    <DetailRow label="Flight" value={row.flight_number} />
                                    <DetailRow label="Flight Return" value={row.flight_number_return} />
                                    <DetailRow label="Airline" value={row.airline} />
                                    <DetailRow label="Airline Return" value={row.airline_return} />
                                    <DetailRow label="Pickup Time" value={row.pickup_time} />
                                    <DetailRow label="Arrival Date" value={row.arrival_date} />
                                    <DetailRow label="Arrival Time" value={row.arrival_time} />
                                    <DetailRow label="Return Time" value={row.return_time} />
                                    <DetailRow label="Luggage" value={row.luggage_amount} />
                                    <DetailRow label="Service Type" value={row.service_type} />
                                    <DetailRow label="Estatus Recogida" value={
                                      <Select value={row.estatus_recojida || "No Confirmado"} onValueChange={v => updateField(row.id, "estatus_recojida", v)}>
                                        <SelectTrigger className={`h-7 w-[140px] text-xs border ${estatusRecogidaColors[row.estatus_recojida || "No Confirmado"] || ""}`}>
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {estatusRecogidaOptions.map(opt => (
                                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    } />
                                    <DetailRow label="Special Requests" value={row.special_requests} />
                                    <DetailRow label="Payment Method" value={paymentMethodLabels[row.payment_method] || row.payment_method} />
                                    <DetailRow label="Transaction ID" value={row.stripe_payment_id ? <span className="font-mono text-xs">{row.stripe_payment_id}</span> : null} />
                                    <DetailRow label="Payment Processor" value={row.payment_processor} />
                                    <DetailRow label="PayPal Transaction" value={row.paypal_transaction_id ? <span className="font-mono text-xs">{row.paypal_transaction_id}</span> : null} />
                                    <DetailRow label="PayPal Payer" value={row.paypal_payer_id ? <span className="font-mono text-xs">{row.paypal_payer_id}</span> : null} />
                                    <DetailRow label="Payment Status" value={
                                      <Select value={row.payment_status} onValueChange={v => updateField(row.id, "payment_status", v)}>
                                        <SelectTrigger className="h-7 w-[120px] text-xs"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="unpaid">Unpaid</SelectItem>
                                          <SelectItem value="pending">Pending</SelectItem>
                                          <SelectItem value="paid">Paid</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    } />
                                    <DetailRow label="Status" value={
                                      <Select value={row.status} onValueChange={v => updateField(row.id, "status", v)}>
                                        <SelectTrigger className={`h-7 w-[130px] text-xs border ${statusColors[row.status] || ""}`}>
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {Object.entries(statusLabels).map(([k, v]) => (
                                            <SelectItem key={k} value={k}>{v}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    } />
                                    <DetailRow label="Total" value={<span className="font-bold text-green-700">${row.total_price}</span>} />
                                    <DetailRow label="Created" value={format(new Date(row.created_at), "PPP 'at' p")} />
                                  </div>
                                  <DialogFooter>
                                    <DialogClose asChild><Button variant="outline">Close</Button></DialogClose>
                                  </DialogFooter>
                                </DialogContent>
                              </Dialog>

                              {row.booking_code && (
                                <Button variant="ghost" size="icon" className="h-7 w-7" asChild>
                                  <a href={`/voucher/${row.booking_code}`} target="_blank" rel="noopener noreferrer" title="View public voucher">
                                    <ExternalLink className="h-4 w-4" />
                                  </a>
                                </Button>
                              )}

                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Booking?</AlertDialogTitle>
                                    <AlertDialogDescription>This action cannot be undone. The booking for {row.customer_name} will be permanently removed.</AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => deleteBooking(row.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {totalPages > 1 && (
            <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground">
                Página {safePage} de {totalPages} · {filteredRows.length} resultados
              </p>
              <Pagination className="mx-0 w-auto justify-end">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      onClick={(e) => { e.preventDefault(); if (safePage > 1) setCurrentPage(safePage - 1); }}
                      className={safePage === 1 ? "pointer-events-none opacity-50" : ""}
                    />
                  </PaginationItem>
                  {pageNumbers.map((p, i) => (
                    <PaginationItem key={`${p}-${i}`}>
                      {p === "ellipsis" ? (
                        <PaginationEllipsis />
                      ) : (
                        <PaginationLink
                          href="#"
                          isActive={p === safePage}
                          onClick={(e) => { e.preventDefault(); setCurrentPage(p as number); }}
                        >
                          {p}
                        </PaginationLink>
                      )}
                    </PaginationItem>
                  ))}
                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      onClick={(e) => { e.preventDefault(); if (safePage < totalPages) setCurrentPage(safePage + 1); }}
                      className={safePage === totalPages ? "pointer-events-none opacity-50" : ""}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </TabsContent>

        <TabsContent value="calendar">
          <BookingsCalendar rows={rows} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
